import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CentrexSiteModel } from '../models/centrex-site.model';
import { ApiService } from './api.service';
import { ServiceBase } from './service-base';
import { shareReplay } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class CentrexSitesService extends ServiceBase {
  private sitesCache = new Map<string, Observable<CentrexSiteModel[]>>();

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
  ) {
    super();
  }

  private createHeaders(additionalHeaders?: {
    [key: string]: string;
  }): HttpHeaders {
    const token = this.apiService.getAccessToken();
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    if (additionalHeaders) {
      Object.keys(additionalHeaders).forEach((key) => {
        headers = headers.set(key, additionalHeaders[key]);
      });
    }

    return headers;
  }

  private handleError(error: any): Observable<never> {
    console.error('Erreur CentrexSitesService', error);
    return throwError(() => error);
  }

  getSites(
    organizationId: string,
    platform: string,
  ): Observable<CentrexSiteModel[]> {
    const cacheKey = `${organizationId}_${platform}`;

    // retourne le cache si déjà chargé
    if (this.sitesCache.has(cacheKey)) {
      return this.sitesCache.get(cacheKey)!;
    }

    const url = `${this.apiUrl}/api/centrex/sites`;

    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    const request$ = this.http.get<CentrexSiteModel[]>(url, { headers }).pipe(
      shareReplay(1),
      catchError((error) => {
        this.sitesCache.delete(cacheKey);
        return this.handleError(error);
      }),
    );

    // stocke dans le cache
    this.sitesCache.set(cacheKey, request$);

    return request$;
  }

  getSiteById(
    organizationId: string,
    platform: string,
    siteId: string,
  ): Observable<CentrexSiteModel> {
    const url = `${this.apiUrl}/api/centrex/sites/${siteId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http
      .get<CentrexSiteModel>(url, { headers })
      .pipe(catchError(this.handleError));
  }

  createSite(
    organizationId: string,
    platform: string,
    site: CentrexSiteModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/sites`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.post<boolean>(url, site, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.sitesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  updateSite(
    organizationId: string,
    platform: string,
    siteId: string,
    site: CentrexSiteModel,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/sites/${siteId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.patch<boolean>(url, site, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.sitesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  deleteSite(
    organizationId: string,
    platform: string,
    siteId: string,
  ): Observable<boolean> {
    const url = `${this.apiUrl}/api/centrex/sites/${siteId}`;
    const headers = this.createHeaders({
      'X-OrganizationId': organizationId,
      'X-CentrexPlatform': platform,
    });

    return this.http.delete<boolean>(url, { headers }).pipe(
      tap(() => {
        const cacheKey = `${organizationId}_${platform}`;

        this.sitesCache.delete(cacheKey);
      }),

      catchError(this.handleError),
    );
  }

  clearCache(): void {
    this.sitesCache.clear();
  }
}
